// ADR 30.9.26h §6 — the learner-settings routes: every `/settings*` call has
// its home here (30.9.26i adds privacy, training and deletion beside these).
//
// The daily review goal is the one learner setting the server holds
// (`users.daily_review_goal`, migration 029). There is no `GET /settings`: the
// value reads back as `daily_goal` on `GET /progress/stats`, so that is the
// read, narrowed to the one field. The write is `PATCH /settings`, behind
// `VerifiedClaims`, which refuses anything outside 1..=100 with a 400
// carrying the server's `daily_review_goal_range` words.

import { authed, authedJson } from './http'

/** The column's default (`029_add_daily_review_goal.sql`), for a stats body without the field. */
const DEFAULT_DAILY_GOAL = 10

/** `GET /progress/stats`, narrowed to `daily_goal`. */
export async function getDailyGoal(): Promise<number> {
  const stats = await authedJson<{ daily_goal?: unknown }>('/progress/stats')
  return typeof stats.daily_goal === 'number' ? stats.daily_goal : DEFAULT_DAILY_GOAL
}

/** The Backend's `UpdateSettings` request (`routes/activity.rs`). */
export interface SettingsPatch {
  daily_review_goal?: number
}

/** The Backend's answer: the goal as stored. */
export interface SettingsResponse {
  daily_review_goal: number
}

/** `PATCH /settings`. */
export function patchSettings(patch: SettingsPatch): Promise<SettingsResponse> {
  return authedJson<SettingsResponse>('/settings', { method: 'PATCH', body: patch })
}

/**
 * ADR 30.9.26i §2.1 — the account row `GET`/`PUT /settings/privacy` serve
 * (Backend `routes/privacy/mod.rs`): always the triple on the way out.
 */
export interface PrivacyRow {
  analytics_opt_out: boolean
  crash_reports_opt_out: boolean
  ads_personalisation_opt_out?: boolean
}

/** Any subset of the triple — the route's request is partial. */
export type PrivacyPatch = Partial<PrivacyRow>

/**
 * `PUT /settings/privacy`, and the route's one writer on the web: the telemetry
 * rows (`consentControl.ts`) and the ads row both come through here.
 */
export function putPrivacy(patch: PrivacyPatch): Promise<PrivacyRow> {
  return authedJson<PrivacyRow>('/settings/privacy', { method: 'PUT', body: patch })
}

/** The Backend's `TrainingOptOutResponse`. */
interface TrainingOptOut {
  opted_out: boolean
}

/** ADR 30.9.26i §2.6 — `GET /settings/training-opt-out`, the read the phones never had. */
export async function getTrainingOptOut(): Promise<boolean> {
  const body = await authedJson<TrainingOptOut>('/settings/training-opt-out')
  return body.opted_out
}

/** `PUT /settings/training-opt-out`; answers the value as stored. */
export async function putTrainingOptOut(optedOut: boolean): Promise<boolean> {
  const body = await authedJson<TrainingOptOut>('/settings/training-opt-out', {
    method: 'PUT',
    body: { opted_out: optedOut },
  })
  return body.opted_out
}

/** ADR 2.10.26d §2 — Backend 2.10.26c §4's one field, request and response alike. */
interface DeveloperToolsRow {
  developer_tools_shown: boolean
}

/** `GET /settings/developer-tools`: the effective value; the web never derives the default. */
export async function getDeveloperTools(): Promise<boolean> {
  return (await authedJson<DeveloperToolsRow>('/settings/developer-tools')).developer_tools_shown
}

/** `PUT /settings/developer-tools`: always an explicit bool (roadmap D2); answers the value as stored. */
export async function putDeveloperTools(shown: boolean): Promise<boolean> {
  const body: DeveloperToolsRow = { developer_tools_shown: shown }
  const row = await authedJson<DeveloperToolsRow>('/settings/developer-tools', {
    method: 'PUT',
    body,
  })
  return row.developer_tools_shown
}

/**
 * ADR 30.9.26i §3 — `POST /account/delete` with an empty body, as
 * `client_account.rs` sends it. Here rather than beside `logout` because
 * `api/auth.ts` is at its line budget.
 */
export async function deleteAccount(): Promise<void> {
  await authed('/account/delete', { method: 'POST', body: {} })
}
